import type { EvalModelId, EvalWorkflowType } from '@root/domain/schemas/evals/eval-config.schema';
import { DEFAULT_EVAL_MODEL_ID } from '@root/domain/schemas/evals/eval-config.schema';

import type { EvalConfigTabId } from './types';

/**
 * Human-readable labels for the supported workflow types. Matches the
 * EvalWorkflowTypeSchema in domain/schemas/evals/eval-config.schema.ts.
 */
export const WORKFLOW_TYPE_LABELS: Record<EvalWorkflowType, string> = {
  LENS_BY_LENS: 'Lens by lens',
};

export const WORKFLOW_TYPE_OPTIONS: { value: EvalWorkflowType; name: string }[] = [
  { value: 'LENS_BY_LENS', name: WORKFLOW_TYPE_LABELS.LENS_BY_LENS },
];

/**
 * Human-readable labels for the supported Bedrock model IDs. Kept in sync
 * with `EvalModelIdSchema` in the domain schema. Both models support the
 * Converse features we rely on (`toolConfig` for structured findings +
 * `cachePoint` for prompt caching). Configs saved before a model was
 * removed from the enum keep their stored id until the model is re-picked.
 */
export const EVAL_MODEL_ID_LABELS: Record<EvalModelId, string> = {
  'eu.anthropic.claude-opus-4-8': 'Claude Opus 4.8 (default)',
  'eu.anthropic.claude-opus-5': 'Claude Opus 5 (newest generation)',
  'eu.anthropic.claude-opus-4-7': 'Claude Opus 4.7 (previous generation)',
};

export const EVAL_MODEL_ID_OPTIONS: { value: EvalModelId; name: string }[] = [
  {
    value: 'eu.anthropic.claude-opus-4-8',
    name: EVAL_MODEL_ID_LABELS['eu.anthropic.claude-opus-4-8'],
  },
  {
    value: 'eu.anthropic.claude-opus-5',
    name: EVAL_MODEL_ID_LABELS['eu.anthropic.claude-opus-5'],
  },
  {
    value: 'eu.anthropic.claude-opus-4-7',
    name: EVAL_MODEL_ID_LABELS['eu.anthropic.claude-opus-4-7'],
  },
];

export { DEFAULT_EVAL_MODEL_ID };

/**
 * Soft UI-side expectations about what prompt-pack roles each workflow type
 * expects. Surfaced as hints in the editor; actual enforcement lives in the
 * orchestration code (increment B). Keeping this in the feature module (not
 * shared) so it can be deleted with the rest of the eval UI.
 */
// Mirrors the starter lens set in scripts/pbqa-eval/starter/ (assembled
// and pushed by seed-starter-config.mjs + push-config.mjs) — keep the
// role list in sync when a lens is added or renamed there.
const LENS_PROMPT_ROLES: { role: string; description: string; required: boolean }[] = [
  {
    role: 'lens:contract-certainty',
    description: 'Is the contract ready to bind? No TBDs, placeholders or unresolved terms.',
    required: false,
  },
  {
    role: 'lens:structure',
    description: 'Does the contract follow MRCv3 section composition and ordering?',
    required: false,
  },
  {
    role: 'lens:consistency',
    description: 'Do values that appear in multiple places agree? Dates, currencies, shares, party names.',
    required: false,
  },
  {
    role: 'lens:clarity',
    description: 'Plain-English readability; would two reasonable readers agree on what the clause means?',
    required: false,
  },
  {
    role: 'lens:regulatory',
    description: "UK Insurance Act 2015, sanctions, applicable law; Lloyd's pre-bind regulatory checks.",
    required: false,
  },
  {
    role: 'lens:placement-structure',
    description:
      'Multi-insurer placement machinery: Subscription Agreement content, claims agreement parties, leader designations, SCAP, BIPAR.',
    required: false,
  },
  // Runs as its own step like any other lens (the planner picks up every
  // `lens:*` role). Client-specific content — each tenant authors its
  // own checklist.
  {
    role: 'lens:client-checklist',
    description: 'Client-defined operating checklist — always-assessed field and reference rules for this tenant.',
    required: false,
  },
];

export const WORKFLOW_PACK_EXPECTATIONS: Record<
  EvalWorkflowType,
  {
    description: string;
    expectedPromptRoles: { role: string; description: string; required: boolean }[];
    expectedGuidelineCount: { min: number; description: string };
  }
> = {
  LENS_BY_LENS: {
    description:
      'Review runs once per lens — each lens is an angle on the whole contract (e.g. contract certainty, structure, consistency). Every lens sees the full contract and all guidelines.',
    expectedPromptRoles: LENS_PROMPT_ROLES,
    expectedGuidelineCount: {
      min: 1,
      description: 'Guidelines are global to the run — all of them are attached to every lens call.',
    },
  },
};

/**
 * Fallback returned for workflow types that no longer exist in the enum
 * (SINGLE_PASS / SECTION_BY_SECTION / LENS_AND_AUDIT rows created before
 * their removal).
 * Stored configs must keep LOADING even when they can no longer run —
 * an unguarded `WORKFLOW_PACK_EXPECTATIONS[type]` lookup white-screens the
 * whole config page for exactly the rows the user most needs to open
 * (to read, rename, archive or migrate them).
 */
const REMOVED_WORKFLOW_EXPECTATIONS: (typeof WORKFLOW_PACK_EXPECTATIONS)[EvalWorkflowType] = {
  description:
    'This workflow type is no longer supported. The config still loads for reference, but runs cannot be started — switch it to a supported workflow type and republish.',
  expectedPromptRoles: [],
  expectedGuidelineCount: { min: 0, description: '' },
};

/** Total-function lookups: never assume a stored workflow_type is a current enum member. */
export const getWorkflowPackExpectations = (
  workflowType: string,
): (typeof WORKFLOW_PACK_EXPECTATIONS)[EvalWorkflowType] =>
  WORKFLOW_PACK_EXPECTATIONS[workflowType as EvalWorkflowType] ?? REMOVED_WORKFLOW_EXPECTATIONS;

export const getWorkflowTypeLabel = (workflowType: string): string =>
  WORKFLOW_TYPE_LABELS[workflowType as EvalWorkflowType] ?? `${workflowType} (no longer supported)`;

export const EVAL_CONFIG_TABS: { id: EvalConfigTabId; label: string }[] = [
  { id: 'settings', label: 'Settings' },
  { id: 'prompt_packs', label: 'Prompt packs' },
  { id: 'guideline_packs', label: 'Guideline packs' },
  { id: 'versions', label: 'Versions' },
];

export const EVAL_CONFIGS_QUERY_KEY = ['eval-configs'] as const;
export const getEvalConfigQueryKey = (id: string) => ['eval-config', id] as const;
export const getEvalConfigVersionQueryKey = (id: string, version: number) =>
  ['eval-config', id, 'version', version] as const;
